import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Bot,
  Clock3,
  Download,
  Expand,
  Image as ImageIcon,
  ImagePlus,
  Lightbulb,
  Loader2,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  User,
  X,
} from 'lucide-react';
import axios from 'axios';
import html2canvas from 'html2canvas';

const MAX_IMAGES_PER_MESSAGE = 4;
const DEFAULT_API_URL = 'http://localhost:8000';

type SelectedPreview = {
  src: string | null;
  name: string;
};

type ChatImageAttachment = {
  src: string;
  title: string | null;
};

type ChatMessageType = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: string;
  images?: SelectedPreview[];
  attachments?: ChatImageAttachment[];
  processingTime?: number;
  isWelcome?: boolean;
};

type ExpandedImage = {
  src: string;
  title: string | null;
} | null;

const ensureAbsoluteUrl = (url: string | null, baseUrl: string): string | null => {
  if (!url) {
    return null;
  }
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) {
    return url;
  }
  const normalizedBase = (baseUrl || '').replace(/\/+$/, '');
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  return `${normalizedBase}${normalizedPath}`;
};

const toDataUrl = (value: string | null | undefined, mimeType = 'image/png'): string | null => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  if (value.startsWith('data:')) {
    return value;
  }
  return `data:${mimeType};base64,${value}`;
};

const extractResponseImages = (responseData: any, baseUrl: string): ChatImageAttachment[] => {
  const pool: any[] = [];

  if (Array.isArray(responseData?.images)) {
    pool.push(...responseData.images);
  }
  if (Array.isArray(responseData?.annotated_images)) {
    pool.push(...responseData.annotated_images);
  }
  if (Array.isArray(responseData?.specialist_analysis?.annotated_images)) {
    pool.push(...responseData.specialist_analysis.annotated_images);
  }

  const normalized = pool
    .map(candidate => {
      if (!candidate) {
        return null;
      }
      if (typeof candidate === 'string') {
        return { src: ensureAbsoluteUrl(candidate, baseUrl), title: null };
      }
      const base64Value =
        candidate.image_base64 || candidate.base64 || candidate.image || candidate.image_data;
      const src = toDataUrl(base64Value) || candidate.image_url || candidate.url || null;

      return {
        src: ensureAbsoluteUrl(src, baseUrl),
        title: candidate.model_name || candidate.model_type || candidate.title || null,
      };
    })
    .filter(Boolean)
    .filter((item: any) => Boolean(item.src));

  const seen = new Set<string>();
  return normalized.filter((item: ChatImageAttachment) => {
    if (seen.has(item.src)) {
      return false;
    }
    seen.add(item.src);
    return true;
  });
};

const createApiClient = (baseUrl: string) => {
  const client = axios.create({
    baseURL: baseUrl || DEFAULT_API_URL,
    timeout: 300000,
    headers: {
      'ngrok-skip-browser-warning': 'true',
    },
  });

  return {
    createChatSession: () => client.post('/api/chat/session'),
    sendChatMessage: (sessionId: string, data: Record<string, unknown>) =>
      client.post(`/api/chat/${sessionId}/message`, data),
    sendChatMessageWithImage: (sessionId: string, formData: FormData) =>
      client.post(`/api/chat/${sessionId}/message-with-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'ngrok-skip-browser-warning': 'true' },
      }),
    sendChatMessageWithImages: (sessionId: string, formData: FormData) =>
      client.post(`/api/chat/${sessionId}/message-with-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'ngrok-skip-browser-warning': 'true' },
      }),
    clearChatHistory: (sessionId: string) => client.post(`/api/chat/session/${sessionId}/clear`),
  };
};

const buildFormData = (files: File[], message: string, useThink: boolean) => {
  const formData = new FormData();
  if (files.length === 1) {
    formData.append('image', files[0]);
  } else {
    files.forEach(file => formData.append('images', file));
  }
  formData.append('message', message || 'Analyze these radiographs.');
  formData.append('use_think', String(useThink));
  formData.append('max_tokens', String(useThink ? 2048 : 1024));
  formData.append('temperature', '0.3');
  return formData;
};

const downloadImage = (src: string, fileName = 'dental-ai-image.png') => {
  const anchor = document.createElement('a');
  anchor.href = src;
  anchor.download = fileName;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export function DentalAIChatPanel() {
  const [api, setApi] = useState<ReturnType<typeof createApiClient> | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [inputText, setInputText] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<SelectedPreview[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useThink, setUseThink] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [apiUrl, setApiUrl] = useState(localStorage.getItem('hulumed_api_url') || '');
  const [expandedImage, setExpandedImage] = useState<ExpandedImage>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (apiUrl) {
      setApi(createApiClient(apiUrl));
      setShowConfig(false);
      return;
    }
    setShowConfig(true);
  }, [apiUrl]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending, scrollToBottom]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 160)}px`;
    }
  }, [inputText]);

  const addSystemMessage = (text: string) => {
    setMessages(prev => [
      ...prev,
      {
        id: `system-${Date.now()}`,
        role: 'assistant',
        text,
        timestamp: new Date().toISOString(),
        isWelcome: true,
      },
    ]);
  };

  const clearAllImages = () => {
    setSelectedImages([]);
    setImagePreviews([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addImageFiles = (files: File[]) => {
    if (!files.length) {
      return;
    }

    const remaining = MAX_IMAGES_PER_MESSAGE - selectedImages.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_IMAGES_PER_MESSAGE} images per message.`);
      return;
    }

    const filesToAdd = files.slice(0, remaining);
    setSelectedImages(prev => [...prev, ...filesToAdd]);

    filesToAdd.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = event => {
          setImagePreviews(prev => [
            ...prev,
            {
              src: (event.target?.result as string) || null,
              name: file.name,
            },
          ]);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreviews(prev => [...prev, { src: null, name: file.name }]);
      }
    });
  };

  const createSession = async () => {
    if (!api) {
      return;
    }
    setSessionLoading(true);
    setError(null);
    try {
      const response = await api.createChatSession();
      setSessionId(response.data.session_id);
      setMessages([]);
      addSystemMessage(
        "Hello! I'm your Dental AI Assistant. Upload radiographs, ask clinical questions, and review specialist model outputs."
      );
    } catch (err) {
      console.error(err);
      setError('Failed to create chat session. Please check API URL and connectivity.');
    } finally {
      setSessionLoading(false);
    }
  };

  const handleCaptureViewport = async () => {
    const rootElement = document.getElementById('root');
    const viewportElement = rootElement?.querySelector('.cornerstone-canvas') as HTMLElement | null;
    if (!viewportElement) {
      setError('No active viewport found for capture.');
      return;
    }

    try {
      const canvas = await html2canvas(viewportElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false,
        removeContainer: true,
        imageTimeout: 0,
      });

      canvas.toBlob(blob => {
        if (!blob) {
          setError('Could not create image from viewport.');
          return;
        }
        const file = new File([blob], 'viewport-capture.png', { type: 'image/png' });
        addImageFiles([file]);
      }, 'image/png');
    } catch (err) {
      console.error(err);
      setError('Failed to capture viewport screenshot.');
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    addImageFiles(files);
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const clearConversation = async () => {
    if (!api || !sessionId) {
      return;
    }
    try {
      await api.clearChatHistory(sessionId);
      setMessages([]);
      addSystemMessage('Conversation cleared.');
    } catch (err) {
      console.error(err);
      setError('Failed to clear conversation.');
    }
  };

  const handleApiConfig = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const urlInput = (form.elements.namedItem('url') as HTMLInputElement | null)?.value?.trim();
    if (!urlInput) {
      setError('Please provide a valid API URL.');
      return;
    }
    localStorage.setItem('hulumed_api_url', urlInput);
    setApiUrl(urlInput);
    setError(null);
  };

  const sendMessage = async (
    event?: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    event?.preventDefault?.();
    if ((!inputText.trim() && selectedImages.length === 0) || sending || !api || !sessionId) {
      return;
    }

    const messageText = inputText.trim();
    const imageFiles = [...selectedImages];
    const userMessage: ChatMessageType = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: messageText,
      timestamp: new Date().toISOString(),
      images: imagePreviews,
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    clearAllImages();
    setSending(true);
    setError(null);

    try {
      let response;
      if (imageFiles.length) {
        const formData = buildFormData(imageFiles, messageText, useThink);
        response =
          imageFiles.length > 1
            ? await api.sendChatMessageWithImages(sessionId, formData)
            : await api.sendChatMessageWithImage(sessionId, formData);
      } else {
        response = await api.sendChatMessage(sessionId, {
          message: messageText,
          use_think: useThink,
          max_tokens: useThink ? 2048 : 1024,
          temperature: 0.3,
        });
      }

      const payload = response?.data || {};
      const attachments = extractResponseImages(payload, apiUrl);
      const assistantMessage: ChatMessageType = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: payload.response || 'No response received.',
        timestamp: new Date().toISOString(),
        processingTime: payload.processing_time_seconds,
        attachments,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error(err);
      setMessages(prev => prev.filter(message => message.id !== userMessage.id));
      setError('Failed to send message. Please retry.');
    } finally {
      setSending(false);
    }
  };

  if (showConfig || !apiUrl) {
    return (
      <div className="flex h-full flex-col bg-[#020617] p-5 text-white">
        <div className="mx-auto mt-6 w-full max-w-md rounded-2xl border border-indigo-500/30 bg-slate-900/95 p-5 shadow-2xl shadow-indigo-500/10">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-400/30 bg-indigo-500/20">
              <Sparkles className="h-5 w-5 text-indigo-300" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">Dental AI Setup</h2>
              <p className="text-xs text-white">Connect your chat API endpoint</p>
            </div>
          </div>
          <form
            onSubmit={handleApiConfig}
            className="space-y-3"
          >
            <label className="block text-xs text-white">API URL</label>
            <input
              name="url"
              defaultValue={apiUrl}
              placeholder="https://your-api-url"
              className="bg-slate-950 w-full rounded-xl border border-slate-700 px-3 py-2 text-sm text-black outline-none transition focus:border-indigo-400"
            />
            <button
              type="submit"
              className="w-full rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-2 text-sm font-semibold text-white transition hover:from-indigo-400 hover:to-violet-400"
            >
              Connect
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="from-slate-950 relative flex h-full flex-col overflow-hidden bg-gradient-to-b to-slate-900 text-white">
      <div className="border-b border-white/10 bg-slate-900/80 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-400/25 bg-gradient-to-br from-indigo-500/40 to-violet-500/30">
              <Bot className="h-5 w-5 text-indigo-100" />
            </div>
            <div>
              <h3 className="text-sm font-semibold tracking-wide text-white">
                Dental AI Assistant
              </h3>
              <p className="text-xs text-white">
                {sessionId ? 'Session active' : 'No active session'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setUseThink(prev => !prev)}
              className={`rounded-lg border p-1.5 text-xs text-white transition ${
                useThink
                  ? 'bg-amber-400/15 border-amber-400/40 text-amber-300'
                  : 'border-slate-700 bg-slate-800/80 text-white hover:text-white'
              }`}
              title="Toggle think mode"
            >
              <Lightbulb className="h-4 w-4" />
            </button>
            <button
              onClick={clearConversation}
              className="rounded-lg border border-slate-700 bg-slate-800/80 p-1.5 text-white transition hover:border-rose-400/40 hover:text-rose-300"
              title="Clear conversation"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowConfig(true)}
              className="rounded-lg border border-slate-700 bg-slate-800/80 p-1.5 text-white transition hover:text-white"
              title="Change API URL"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="scrollbar-thin flex-1 overflow-y-auto px-4 py-4">
        {!sessionId && !sessionLoading && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-indigo-400/30 bg-gradient-to-br from-indigo-500/30 to-fuchsia-500/20 shadow-lg shadow-indigo-500/10">
              <Sparkles className="h-8 w-8 text-indigo-200" />
            </div>
            <h2 className="text-lg font-semibold text-white">Start Dental Analysis Session</h2>
            <p className="mt-2 max-w-sm text-sm text-white">
              Ask questions, upload radiographs, and review model-generated annotated images in one
              place.
            </p>
            <button
              onClick={createSession}
              className="mt-5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:from-indigo-400 hover:to-violet-400"
            >
              Start Session
            </button>
          </div>
        )}

        {sessionLoading && (
          <div className="flex h-full items-center justify-center text-indigo-300">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map(message => (
            <ChatMessage
              key={message.id}
              message={message}
              onExpandImage={setExpandedImage}
            />
          ))}
        </AnimatePresence>

        {sending && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 flex items-start gap-3"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div className="rounded-2xl rounded-tl-none border border-white/10 bg-slate-800/80 px-4 py-3">
              <div className="flex items-center gap-2 text-xs text-white">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Analyzing radiograph...
              </div>
            </div>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {imagePreviews.length > 0 && (
        <div className="border-t border-white/10 px-4 py-2">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {imagePreviews.map((preview, index) => (
              <div
                key={`${preview.name}-${index}`}
                className="group relative overflow-hidden rounded-xl border border-white/10 bg-slate-900/80"
              >
                {preview.src ? (
                  <img
                    src={preview.src}
                    alt={preview.name || 'Selected image'}
                    className="h-20 w-full object-cover"
                  />
                ) : (
                  <div className="flex h-20 items-center justify-center text-xs text-white">
                    {preview.name || 'DICOM file'}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute right-1 top-1 rounded-full border border-black/30 bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="px-4 py-2 text-xs text-white">
          <div className="flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            {error}
          </div>
        </div>
      )}

      {sessionId && (
        <div className="border-t border-white/10 bg-slate-900/75 px-4 py-3 backdrop-blur">
          <form
            onSubmit={sendMessage}
            className="flex items-end gap-2"
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              accept="image/*,.dcm"
              multiple
              className="hidden"
            />
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-lg border border-slate-700 bg-slate-800 p-1 text-white transition hover:border-indigo-400/40 hover:text-indigo-200"
                title="Upload image"
              >
                <ImagePlus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={handleCaptureViewport}
                className="rounded-lg border border-slate-700 bg-slate-800 p-1 text-white transition hover:border-indigo-400/40 hover:text-indigo-200"
                title="Capture active viewport"
              >
                <ImageIcon className="h-3.5 w-3.5" />
              </button>
            </div>
            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={event => setInputText(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  sendMessage(event);
                }
              }}
              placeholder="Ask a diagnostic question..."
              rows={1}
              className="bg-slate-950 min-h-16 w-full resize-none rounded-xl border border-slate-700 px-3 py-2 text-sm text-black outline-none transition focus:border-indigo-400"
            />
            <button
              type="submit"
              disabled={(!inputText.trim() && selectedImages.length === 0) || sending}
              className="rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 p-2.5 text-white transition hover:from-indigo-400 hover:to-violet-400 disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </form>
        </div>
      )}

      <AnimatePresence>
        {expandedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-black/85 fixed inset-0 z-[120] flex items-center justify-center p-4"
            onClick={() => setExpandedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0.8 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.97, opacity: 0.8 }}
              className="w-full max-w-5xl overflow-hidden rounded-2xl border border-white/10 bg-slate-900"
              onClick={event => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-white/10 bg-slate-800 px-4 py-2 text-xs text-white">
                <span className="truncate pr-3">{expandedImage.title || 'AI Image Preview'}</span>
                <div className="flex items-center gap-2">
                  <button
                    className="rounded-md border border-slate-600/60 bg-slate-800 px-2 py-1 transition hover:text-indigo-200"
                    onClick={() => downloadImage(expandedImage.src)}
                  >
                    Download
                  </button>
                  <button
                    className="rounded-md border border-slate-600/60 bg-slate-800 p-1 transition hover:text-indigo-200"
                    onClick={() => setExpandedImage(null)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="max-h-[80vh] overflow-auto bg-slate-800 p-3">
                <img
                  src={expandedImage.src}
                  alt={expandedImage.title || 'Expanded AI image'}
                  className="mx-auto h-[400px] max-w-full rounded-lg object-contain"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ChatMessage({
  message,
  onExpandImage,
}: {
  message: ChatMessageType;
  onExpandImage: (image: ExpandedImage) => void;
}) {
  const isUser = message.role === 'user';
  const [expandedThinking, setExpandedThinking] = useState(false);

  let thinking: string | null = null;
  let content = message.text || '';
  const thinkMatch = content.match(/<think>([\s\S]*?)<\/think>/);
  if (thinkMatch) {
    thinking = thinkMatch[1];
    content = content.replace(thinkMatch[0], '').trim();
  }

  const attachments = message.attachments || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mb-4 flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
    >
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
          isUser
            ? 'bg-gradient-to-br from-emerald-500 to-green-600'
            : 'bg-gradient-to-br from-indigo-500 to-violet-600'
        }`}
      >
        {isUser ? <User className="h-4 w-4 text-white" /> : <Bot className="h-4 w-4 text-white" />}
      </div>

      <div className={`flex max-w-[88%] flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        {!!message.images?.length && (
          <div className="mb-2 grid w-full grid-cols-2 gap-2">
            {message.images.map((image, index) => (
              <div
                key={`${index}-${image.name}`}
                className="overflow-hidden rounded-xl border border-white/10 bg-slate-900/90"
              >
                {image.src ? (
                  <img
                    src={image.src}
                    alt={image.name || 'uploaded'}
                    className="h-24 w-full object-cover"
                  />
                ) : (
                  <div className="flex h-24 items-center justify-center px-2 text-center text-xs text-white">
                    {image.name || 'DICOM file'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!!thinking && !isUser && (
          <div className="mb-2 w-full">
            <button
              onClick={() => setExpandedThinking(prev => !prev)}
              className="flex items-center gap-1 text-xs text-amber-300 transition hover:text-amber-200"
            >
              <Lightbulb className="h-3.5 w-3.5" />
              AI reasoning
            </button>
            {expandedThinking && (
              <div className="mt-1 whitespace-pre-wrap rounded-xl border border-amber-300/20 bg-amber-300/10 p-2 text-xs text-amber-100/90">
                {thinking}
              </div>
            )}
          </div>
        )}

        <div
          className={`w-full whitespace-pre-wrap rounded-2xl border px-3 py-2.5 text-sm leading-6 text-white ${
            isUser
              ? 'rounded-tr-none border-emerald-300/25 bg-emerald-400/10 text-emerald-50'
              : 'rounded-tl-none border-white/10 bg-slate-800/70 text-white'
          }`}
        >
          {content}
        </div>

        {!isUser && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-white">
            {!!message.processingTime && (
              <span className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/70 px-1.5 py-0.5">
                <Clock3 className="h-3 w-3" />
                {message.processingTime.toFixed(1)}s
              </span>
            )}
            {!!attachments.length && (
              <span className="rounded-md border border-slate-700 bg-slate-800/70 px-1.5 py-0.5">
                {attachments.length} image{attachments.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {!!attachments.length && (
          <div className="mt-2 grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
            {attachments.map((item, index) => (
              <div
                key={`${item.src}-${index}`}
                className="group overflow-hidden rounded-xl border border-white/10 bg-slate-900/90"
              >
                <div className="relative">
                  <img
                    src={item.src}
                    alt={item.title || `AI attachment ${index + 1}`}
                    className="h-32 w-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 transition group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => onExpandImage(item)}
                      className="bg-black/55 rounded-md border border-white/20 p-1.5 text-white"
                      title="Enlarge image"
                    >
                      <Expand className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        downloadImage(item.src, `${item.title || 'dental-ai'}-${index + 1}.png`)
                      }
                      className="bg-black/55 rounded-md border border-white/20 p-1.5 text-white"
                      title="Download image"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {!!item.title && (
                  <div className="truncate border-t border-white/10 px-2 py-1 text-[11px] text-white">
                    {item.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
